import React, { Dispatch, SetStateAction, useEffect } from 'react';
import { FaExclamationCircle } from 'react-icons/fa';

import { UseOutletContextStorageCenter } from '../StorageCenter';
import { StateLanguage } from '../../../../../languages/config/StateLanguage';
import { StorageCenter } from '../../../../../models/storage.center.model';
import { prefixLegend } from '../../../../../scripts/name.legend.script';
import { expressions } from '../../../../../libraries/regular.expressions.library';
import { evaluateLegendOptionalIcon, evaluateLegendRequiredIcon } from '../../../../../scripts/validate.legend.script';
import { uploadTooltip } from '../../../../../utils/tooltip.util';

import AppLegend from '../../../../../components/element/Legend';
import AppInputCountry from '../../../../../components/element/InputCountry';

export interface AppStorageCenterFormProps {
  component?: string,
  storageCenter?: StorageCenter,
  name: {value: string, valid: boolean},
  street: {value: string, valid: boolean},
  streetNumber: {value: string, valid: boolean},
  additional: {value: string, valid: boolean},
  administrativeArea: {value: string, valid: boolean},
  city: {value: string, valid: boolean},
  zipCode: {value: string, valid: boolean},
  country: {value: string, valid: boolean},
  isSav: {value: boolean, valid: boolean},
  setName: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setStreet: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setStreetNumber: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setAdditional: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setAdministrativeArea: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setCity: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setZipCode: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setCountry: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setIsSav: Dispatch<SetStateAction<{value: boolean, valid: boolean}>>
};

const AppStorageCenterForm: React.FunctionComponent<AppStorageCenterFormProps> = ({component, name, street, streetNumber, additional, administrativeArea, city, zipCode, country, isSav, setName, setStreet, setStreetNumber, setAdditional, setAdministrativeArea, setCity, setZipCode, setCountry, setIsSav}) => {
  const {companyForUser} = UseOutletContextStorageCenter()
  
  const {lang} = StateLanguage()

  const handleChangeName = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setName({...name, value: event.target.value})
  }

  const handleChangeStreet = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
      setStreet({...street, value: event.target.value})
    }
  
  const handleChangeStreetNumber = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setStreetNumber({...streetNumber, value: event.target.value})
  }

  const handleChangeAdditional = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setAdditional({...additional, value: event.target.value})
  }

  const handleChangeAdministrativeArea = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setAdministrativeArea({...administrativeArea, value: event.target.value})
  }

  const handleChangeCity = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setCity({...city, value: event.target.value})
  }

  const handleChangeZipCode = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setZipCode({...zipCode, value: event.target.value})
  }

  const validateName = () => {
    evaluateLegendRequiredIcon(expressions.name, name, setName, `${prefixLegend(component)}input-name`, `${prefixLegend(component)}legend-name-valid`, `${prefixLegend(component)}legend-name-required`)
  }

  const validateStreet = () => {
      evaluateLegendRequiredIcon(expressions.street, street, setStreet, `${prefixLegend(component)}input-street`, `${prefixLegend(component)}legend-street-valid`, `${prefixLegend(component)}legend-street-required`)
    }
  
  const validateStreetNumber = () => {
    evaluateLegendOptionalIcon(expressions.streetnumber, streetNumber, setStreetNumber, `${prefixLegend(component)}input-street-number`, `${prefixLegend(component)}legend-street-number-valid`)
  }

  const validateAdditional = () => {
    evaluateLegendOptionalIcon(expressions.text, additional, setAdditional, `${prefixLegend(component)}input-additional`, `${prefixLegend(component)}legend-additional-valid`)
  }

  const validateAdministrativeArea = () => {
    evaluateLegendRequiredIcon(expressions.location, administrativeArea, setAdministrativeArea, `${prefixLegend(component)}input-administrative-area`, `${prefixLegend(component)}legend-administrative-area-valid`, `${prefixLegend(component)}legend-administrative-area-required`)
  }

  const validateCity = () => {
    evaluateLegendRequiredIcon(expressions.location, city, setCity, `${prefixLegend(component)}input-city`, `${prefixLegend(component)}legend-city-valid`, `${prefixLegend(component)}legend-city-required`)
  }

  const validateZipCode = () => {
    evaluateLegendOptionalIcon(expressions.zipcode, zipCode, setZipCode, `${prefixLegend(component)}input-zip-code`, `${prefixLegend(component)}legend-zip-code-valid`)
  }

  const handleChangeIsSav = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsSav({value: event.target.checked, valid: true})
  }

  useEffect( () => {
    uploadTooltip()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  return (
    <div className="form">
      <div className="fv-row mb-5">
        <label className="form-label required">{lang.labels.name}</label>
        <input id={`${prefixLegend(component)}input-name`} className="form-control" type="text" autoComplete="off" name="name" value={name.value} onChange={handleChangeName} onKeyUp={validateName} onBlur={validateName} />
        <AppLegend component={component} attribute={{check: name.valid, name: "name", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
      </div>
      <div className="row fv-row mb-5">
        <div className="col-xl-8 mb-5 mb-xl-0">
          <label className="form-label required">{lang.labels.street}</label>
          <input id={`${prefixLegend(component)}input-street`} className="form-control" type="text" autoComplete="off" name="street" value={street.value} onChange={handleChangeStreet} onKeyUp={validateStreet} onBlur={validateStreet} />
          <AppLegend component={component} attribute={{check: street.valid, name: "street", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
        </div>
        <div className="col-xl-4">
          <label className="form-label">
            <div className="d-flex align-items-center">
              {lang.labels.number}
              <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.whenNumberIsNotEntered}><FaExclamationCircle /></i>
            </div>
          </label>
          <input id={`${prefixLegend(component)}input-street-number`} className="form-control" type="text" autoComplete="off" name="street-number" placeholder={lang.labels.noNumber} value={streetNumber.value} onChange={handleChangeStreetNumber} onKeyUp={validateStreetNumber} onBlur={validateStreetNumber} />
          <AppLegend component={component} attribute={{check: streetNumber.valid, name: "street-number", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
        </div>
      </div>
      <div className="fv-row mb-5">
        <label className="form-label">
          <div className="d-flex align-items-center">
            {lang.labels.additional}
            <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.ifNecessaryEnterSomethingReferentialRegardingAddress}><FaExclamationCircle /></i>
          </div>
        </label>
        <input id={`${prefixLegend(component)}input-additional`} className="form-control" type="text" autoComplete="off" name="additional" value={additional.value} onChange={handleChangeAdditional} onKeyUp={validateAdditional} onBlur={validateAdditional} />
        <AppLegend component={component} attribute={{check: additional.valid, name: "additional", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
      </div>
      <div className="fv-row mb-5">
        <label className="form-label required">{lang.labels.administrativeArea}</label>
        <input id={`${prefixLegend(component)}input-administrative-area`} className="form-control" type="text" autoComplete="off" name="administrative-area" value={administrativeArea.value} onChange={handleChangeAdministrativeArea} onKeyUp={validateAdministrativeArea} onBlur={validateAdministrativeArea} />
        <AppLegend component={component} attribute={{check: administrativeArea.valid, name: "administrative-area", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
      </div>
      <div className="row fv-row mb-5">
        <div className="col-xl-4 mb-5 mb-xl-0">
          <label className="form-label">
            <div className="d-flex align-items-center">
              {lang.labels.zipCode}
              <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.enterThePostalCodeOfTheCity}><FaExclamationCircle /></i>
            </div>
          </label>
          <input id={`${prefixLegend(component)}input-zip-code`} className="form-control" type="text" autoComplete="off" name="zip-code" value={zipCode.value} onChange={handleChangeZipCode} onKeyUp={validateZipCode} onBlur={validateZipCode} />
          <AppLegend component={component} attribute={{check: zipCode.valid, name: "zip-code", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
        </div>
        <div className="col-xl-8">
          <label className="form-label required">{lang.labels.city}</label>
          <input id={`${prefixLegend(component)}input-city`} className="form-control" type="text" autoComplete="off" name="city" value={city.value} onChange={handleChangeCity} onKeyUp={validateCity} onBlur={validateCity} />
          <AppLegend component={component} attribute={{check: city.valid, name: "city", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
        </div>
      </div>
      <div className="fv-row mb-5">
        <label className="form-label required">{lang.labels.country}</label>
        <AppInputCountry component={component} options={{small: false, disabled: false, placeholder: null}} country={country} setCountry={setCountry}></AppInputCountry>
      </div>
      <div className="fv-row">
        <div className="d-flex flex-stack">
          <div className="d-flex flex-column">
            <label className="form-label mb-0">{lang.labels.afterSalesService}</label>
          </div>
          <div className="d-flex justify-content-end">
            <div className="form-check form-check-solid form-check-custom form-switch">
              <input className="form-check-input w-50px h-30px" type="checkbox" checked={isSav.value} onChange={handleChangeIsSav} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
};

export default AppStorageCenterForm;
